import React, { memo, useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { getRadioTestIdByText } from "e2e-tests/testIds";

import { logAnalyticsEvent, setAnalyticsUserProperties } from "@/constants/amplitude";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { ExperimentIds } from "@/constants/experiments";
import { sendMetrics } from "@/constants/gtag";
import { QUESTIONS_IDS, QuestionTypes } from "@/constants/questionsIds";
import { HandleNext } from "@/types/common";
import { Question } from "@/types/question";
import { useExperiment } from "@/utils/ExperimentsProvider";

import useAnswers from "../../utils/useAnswers";
import useUserDataConsent from "../UserConsent";

interface RadioImageLeftProps {
    question: Question<QuestionTypes.RadioImageLeft>;
    handleNext: HandleNext;
    isActive: boolean;
}
const RadioImageLeft = ({ question, handleNext, isActive }: RadioImageLeftProps) => {
    const { setAnswers } = useAnswers();
    const [isAnswered, setIsAnswered] = useState(false);
    const [selectedAnsId, setSelectedAnsId] = useState("");
    const [isDisabledButton, setIsDisabledButton] = useState(false);

    const [needsRerender, setNeedsRerender] = useState(false);

    const handlePolicyConsentClick = () => {
        goToNextQuestion(selectedAnsId);
    };

    const { Component: ConsentComponent, isConsent } = useUserDataConsent({
        event: question.event,
        onConsent: handlePolicyConsentClick,
        withScrollToConsent: question.answers.length > 2,
    });

    const { isTest: isNewQsSequenceExp } = useExperiment({
        experimentId: ExperimentIds.NEW_QS_SEQUENCE,
    });

    const withConsent = question.id === QUESTIONS_IDS.PRIMARY_GOAL && !isNewQsSequenceExp;
    const canProceed = (withConsent && isConsent) || !withConsent;

    const goToNextQuestion = useCallback(
        (ansId: string) => {
            logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
                [WebEventProps.RESULT]: ansId,
            });
            handleNext({ [question.id]: ansId });
            setAnalyticsUserProperties({ [question.id]: ansId });
            sendMetrics(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
                [WebEventProps.RESULT]: ansId,
            });
        },
        [handleNext, question.event, question.id]
    );

    useEffect(() => {
        if (isActive) {
            setIsDisabledButton(false);
        }
    }, [isActive]);

    const handleContinue = (ansId: string) => {
        setIsDisabledButton(() => {
            return !(question.id === QUESTIONS_IDS.PRIMARY_GOAL && !canProceed);
        });
        if (question.id === QUESTIONS_IDS.LOCATION) {
            setAnswers((prev) => ({ ...prev, [question.id]: ansId }));
        }
        if (question.id === QUESTIONS_IDS.PRIMARY_GOAL && !canProceed) {
            setIsAnswered(true);
            setAnswers((prev) => ({ ...prev, [question.id]: ansId }));
        } else if (question.id === QUESTIONS_IDS.PRIMARY_GOAL && canProceed && !needsRerender) {
            setIsAnswered(true);
            setAnswers((prev) => ({ ...prev, [question.id]: ansId }));
            setNeedsRerender(true);
        } else {
            goToNextQuestion(ansId);
        }
        setSelectedAnsId(ansId);
    };

    useEffect(() => {
        if (needsRerender && isAnswered && selectedAnsId) {
            setNeedsRerender(false);
            goToNextQuestion(selectedAnsId);
        }
    }, [needsRerender, isAnswered, selectedAnsId, goToNextQuestion]);

    const renderAnswers = () => {
        return question.answers.map((ans) => {
            return (
                <div
                    key={ans.id}
                    className={classNames(
                        "flex flex-row-reverse gap-4 rounded-3xl p-2 pr-4 text-zing-18-26 text-white hover:cursor-pointer",
                        {
                            "items-center justify-end": ans.img || ans.svg,
                            "bg-zing-cool-blue-600 female:bg-zing-warm-tangerine-400 female:text-white":
                                ans.id === selectedAnsId,
                            "pointer-events-none": isDisabledButton,
                            "bg-zing-black-200 female:bg-zing-warm-tangerine-100 female:text-black":
                                ans.id !== selectedAnsId,
                        }
                    )}
                    onClick={() => handleContinue(ans.id)}
                    data-zing-test-id={getRadioTestIdByText(ans.text)}
                >
                    <div className="flex flex-col">
                        <div className="font-semibold">{ans.text}</div>
                        {ans.description && (
                            <div
                                className={classNames("text-zing-12-18 text-zing-gray-100", {
                                    "female:text-white": ans.id === selectedAnsId,
                                    "female:text-zing-neutral-cool-grey-600":
                                        ans.id !== selectedAnsId,
                                })}
                            >
                                {ans.description}
                            </div>
                        )}
                    </div>
                    {ans.img && (
                        <img
                            src={ans.img}
                            className="h-[86px] w-[86px] rounded-2xl bg-white"
                            alt=""
                        />
                    )}
                    {ans.svg && (
                        <div
                            className={classNames(
                                "my-4 ml-4 flex h-[40px] w-[40px] items-center justify-center"
                            )}
                        >
                            <ans.svg
                                color={classNames({
                                    "color-white": ans.id === selectedAnsId,
                                })}
                            />
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <div className="grid h-full grow content-between gap-10">
            <div className="grid gap-6 pb-6">
                <div className="grid gap-4">
                    <div
                        className={classNames(
                            "whitespace-pre-line text-zing-24-32 font-semibold text-white female:text-black"
                        )}
                    >
                        {question?.title}
                    </div>
                    {question.subtitle && (
                        <div className="whitespace-pre-line text-zing-16-24 text-white female:text-black">
                            {question.subtitle}
                        </div>
                    )}
                </div>
                <div className="grid gap-2">{renderAnswers()}</div>
                {question.extraBottomComponent}
            </div>
            {withConsent && <ConsentComponent canProceed={isAnswered} className="pb-10" />}
        </div>
    );
};

export default memo(RadioImageLeft);
